import { DEFAULT_OPENER_STYLE_ID, OPENER_KIND_INFO } from "@loot-os/api-contract";
import type { LootTheme } from "@loot-os/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { EyeIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { OpenerDefaultBadge } from "../lib/openerDefaults";
import type { OpenerStyleChoice } from "../lib/openerStyles";
import { OpenerKindArt } from "./OpenerKindArt";
import { OpenerStylePreview } from "./OpenerStylePreview";

/** How many of a style's own colors its card shows before it counts the rest. */
const SWATCH_LIMIT = 6;

/**
 * Every color a style sets, shared roles first, then rarity, then its kind variables, each keyed
 * by where it is set (`colors.accent`).
 */
function styleColors(style: OpenerStyleChoice): { key: string; color: string }[] {
  const { colors, rarity, kind } = style.theme;
  return Object.entries({ colors, rarity, kind }).flatMap(([group, values]) =>
    Object.entries(values ?? {}).flatMap(([name, color]) =>
      typeof color === "string" ? [{ key: `${group}.${name}`, color }] : [],
    ),
  );
}

/**
 * The opener styles a scope can use, as a grid of cards: each kind's outline drawing, the
 * colors the style sets, the defaults it opens with marked, and a quick preview of the real
 * opener over `hostTheme`. Links to the editor page for the styles the scope owns
 * (`editableScope`): platform staff own platform styles, a tenant editor the environment's. The
 * rest are listed read-only, and preview all the same.
 */
export function OpenerStylesPanel({
  styles,
  editableScope,
  defaults,
  hostTheme,
  logoSvg,
  brandName,
  canEdit,
  editLink,
  newLink,
  onDelete,
}: {
  styles: readonly OpenerStyleChoice[];
  editableScope: OpenerStyleChoice["scope"];
  /** The styles this scope's boxes open with when they name none (`openerDefaultBadges`). */
  defaults: readonly OpenerDefaultBadge[];
  /** What the preview themes the opener with before the style's overrides; undefined = its own palette. */
  hostTheme: LootTheme | undefined;
  /** The environment's opener logo, which every preview wears; omitted in the platform scope. */
  logoSvg?: string;
  /** The environment's opener brand name, which the preview prints without a logo. */
  brandName?: string;
  canEdit: boolean;
  /** The Edit link to one style's editor page. */
  editLink: (styleId: string) => ReactNode;
  /** The New style link to the editor page. */
  newLink: ReactNode;
  onDelete: (styleId: string) => void;
}) {
  const [deleting, setDeleting] = useState<OpenerStyleChoice | null>(null);
  // Kept after close, so the dialog still shows the style while it animates out.
  const [previewing, setPreviewing] = useState<OpenerStyleChoice | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  return (
    <>
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {styles.map((style) => {
          const editable = canEdit && style.scope === editableScope;
          const colors = styleColors(style);
          const custom = Object.keys(style.theme).length > 0;
          return (
            <li
              key={style.id}
              className="flex flex-col overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10"
            >
              <div className="relative flex aspect-[2/1] items-center justify-center border-b bg-muted/40 text-muted-foreground">
                <OpenerKindArt kind={style.kind} className="size-24" />
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  {defaults
                    .filter((badge) => badge.styleId === style.id)
                    .map((badge) => (
                      <Badge key={badge.label}>{badge.label}</Badge>
                    ))}
                  {style.scope === "platform" && editableScope === "environment" && (
                    <Badge variant="outline" className="bg-background">
                      Platform
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="outline"
                  className="absolute top-2.5 right-2.5 bg-background"
                  aria-label={`Preview ${style.name}`}
                  title="Preview"
                  onClick={() => {
                    setPreviewing(style);
                    setPreviewOpen(true);
                  }}
                >
                  <EyeIcon />
                </Button>
              </div>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="grid min-w-0 gap-0.5">
                  <span className="truncate font-medium">{style.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {OPENER_KIND_INFO[style.kind].label}
                  </span>
                </div>
                <div className="flex min-h-5 items-center gap-1.5">
                  {colors.length > 0 ? (
                    <>
                      {colors.slice(0, SWATCH_LIMIT).map(({ key, color }) => (
                        <span
                          key={key}
                          className="size-4 rounded-full ring-1 ring-foreground/15"
                          style={{ backgroundColor: color }}
                          title={`${key}: ${color}`}
                        />
                      ))}
                      {colors.length > SWATCH_LIMIT && (
                        <span className="text-xs text-muted-foreground">
                          +{colors.length - SWATCH_LIMIT}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {custom ? "Custom radius or font" : "Follows the theme"}
                    </span>
                  )}
                </div>
                {editable && (
                  <div className="mt-auto flex items-center gap-2 border-t pt-3">
                    {editLink(style.id)}
                    {/* The classic spinner is every box's fallback, and the server refuses its delete. */}
                    {style.id !== DEFAULT_OPENER_STYLE_ID && (
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        className="ml-auto text-muted-foreground"
                        aria-label={`Delete ${style.name}`}
                        title="Delete"
                        onClick={() => setDeleting(style)}
                      >
                        <Trash2Icon />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
        {canEdit && (
          <li className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-4 text-center text-muted-foreground">
            <PlusIcon className="size-6" />
            <span className="text-sm">An animation and the colors it overrides.</span>
            {newLink}
          </li>
        )}
      </ul>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
          {previewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
                  {previewing.name}
                  <Badge variant="outline">{OPENER_KIND_INFO[previewing.kind].label}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {OPENER_KIND_INFO[previewing.kind].description} Opens roll here and nothing is
                  saved.
                </DialogDescription>
              </DialogHeader>
              <OpenerStylePreview
                key={previewing.id}
                kind={previewing.kind}
                hostTheme={hostTheme}
                overrides={previewing.theme}
                logoSvg={logoSvg}
                brandName={brandName}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              A style something still opens with cannot be deleted: move those boxes, shops and
              defaults to another style first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting) onDelete(deleting.id);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
